import type {
  AuthUser,
  GetUserByEmailParams,
  GetUsersParams,
  ListResponseDto,
  ResponseDto,
  UpdateUserParams,
} from "@/apps/admin/api/authTypes";
import {
  parseAuthUserListResponse,
  parseAuthUserResponse,
  parseNullableAuthUserResponse,
} from "@/apps/admin/api/authValidation";
import api, { TAG_TYPES } from "@/state/api";
import { providesTagsList } from "@/state/utilities/rtkToolkit";

const API_ROUTE = "auth/api/v1";

const extendedApi = api.injectEndpoints({
  endpoints: (build) => ({
    getObjectUsers: build.query<ListResponseDto<AuthUser>, GetUsersParams>({
      query: ({ objectType, objectId }) =>
        `${API_ROUTE}/${objectType}/${objectId}/users`,
      transformResponse: parseAuthUserListResponse,
      providesTags: providesTagsList(TAG_TYPES.OBJECT_USER, { detail: true }),
    }),
    getObjectUserByEmail: build.query<
      ResponseDto<AuthUser>,
      GetUserByEmailParams
    >({
      query: ({ objectType, objectId, email }) =>
        `${API_ROUTE}/${objectType}/${objectId}/users/${encodeURIComponent(
          email
        )}`,
      transformResponse: parseAuthUserResponse,
      // A 403 means "this user has no admin access", which the UI shows on its own page.
      extraOptions: { notify: "unless403" },
      // `updateObjectUser` invalidates the USER list and numeric ids, not emails
      providesTags: (result, error, args) => [
        { type: TAG_TYPES.OBJECT_USER, id: args.email },
        ...providesTagsList(TAG_TYPES.OBJECT_USER, { detail: true })(
          result,
          error,
          args
        ),
      ],
    }),
    updateObjectUser: build.mutation<
      ResponseDto<AuthUser | null>,
      UpdateUserParams
    >({
      query: ({ objectType, objectId, userId, user }) => ({
        url: `${API_ROUTE}/${objectType}/${objectId}/users/${encodeURIComponent(
          userId
        )}`,
        method: "PUT",
        body: user,
      }),
      transformResponse: parseNullableAuthUserResponse,
      invalidatesTags: providesTagsList(TAG_TYPES.OBJECT_USER, {
        detail: true,
      }),
    }),
  }),
});

export const {
  useGetObjectUsersQuery,
  useGetObjectUserByEmailQuery,
  useUpdateObjectUserMutation,
} = extendedApi;

export { extendedApi as usersApi };
